import React, { useCallback, useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const call = (name, data) => httpsCallable(getFunctions(), name)(data).then(r => r.data);

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

const AdminBlockedUsers = () => {
  const [identifier, setIdentifier] = useState('');
  const [users, setUsers]           = useState([]);
  const [listing, setListing]       = useState(true);
  const [busy, setBusy]             = useState('');
  const [message, setMessage]       = useState('');
  const [error, setError]           = useState('');

  const [lookupId, setLookupId]       = useState('');
  const [lookupBusy, setLookupBusy]   = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    const id = lookupId.trim();
    if (!id) return;
    setLookupBusy(true);
    setLookupResult(null);
    setLookupError('');
    try {
      const res = await call('lookupUser', { identifier: id });
      setLookupResult(res.user);
    } catch (err) {
      setLookupError(err.message || 'Lookup failed');
    }
    setLookupBusy(false);
  };

  const [ips, setIps]         = useState([]);
  const [ipInput, setIpInput] = useState('');
  const [ipNote, setIpNote]   = useState('');
  const [ipBusy, setIpBusy]   = useState('');
  const [ipMessage, setIpMessage] = useState('');
  const [ipError, setIpError] = useState('');

  const loadIps = useCallback(async () => {
    try {
      setIps((await call('listBlockedIps')).ips);
    } catch (err) {
      setIpError(err.message || 'Failed to load blocked IPs');
    }
  }, []);

  const ipAct = async (fn, payload, okMsg) => {
    setIpMessage('');
    setIpError('');
    setIpBusy(payload.ip);
    try {
      await call(fn, payload);
      setIpMessage(okMsg);
      await loadIps();
      return true;
    } catch (err) {
      setIpError(err.message || 'Request failed');
      return false;
    } finally {
      setIpBusy('');
    }
  };

  const handleBlockIp = async (e) => {
    e.preventDefault();
    const ip = ipInput.trim();
    if (!ip) return;
    if (!window.confirm(`Block IP ${ip}? Anyone on this address will be refused by the app's functions (shared or mobile IPs may affect other people).`)) return;
    const ok = await ipAct('blockIp', { ip, note: ipNote }, `Blocked ${ip}`);
    if (ok) { setIpInput(''); setIpNote(''); }
  };

  const handleUnblockIp = (ip) => {
    if (!window.confirm(`Unblock IP ${ip}?`)) return;
    ipAct('unblockIp', { ip }, `Unblocked ${ip}`);
  };

  const load = useCallback(async () => {
    setListing(true);
    try {
      const res = await call('listBlockedUsers');
      setUsers(res.users);
    } catch (err) {
      setError(err.message || 'Failed to load blocked users');
    }
    setListing(false);
  }, []);

  useEffect(() => { load(); loadIps(); }, [load, loadIps]);

  const act = async (fn, id, okMsg) => {
    setMessage('');
    setError('');
    setBusy(id);
    try {
      const res = await call(fn, { identifier: id });
      setMessage(okMsg(res.user));
      await load();
      return true;
    } catch (err) {
      setError(err.message || 'Request failed');
      return false;
    } finally {
      setBusy('');
    }
  };

  const handleBlock = async (e) => {
    e.preventDefault();
    const id = identifier.trim();
    if (!id) {
      setError('Enter a UID or email');
      return;
    }
    if (!window.confirm(`Block ${id}? They will be signed out and unable to create previews or invitations.`)) return;
    const ok = await act('blockUser', id, u => `Blocked ${u.email || u.uid}`);
    if (ok) setIdentifier('');
  };

  const handleUnblock = (u) => {
    if (!window.confirm(`Unblock ${u.email || u.uid}?`)) return;
    act('unblockUser', u.uid, x => `Unblocked ${x.email || x.uid}`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Look Up User</h2>
        <p className="text-gray-600 mb-4">Find a user's email from their UID, or the UID from their email.</p>
        <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="User UID or email"
            disabled={lookupBusy}
          />
          <button
            type="submit"
            disabled={lookupBusy || !lookupId.trim()}
            className="bg-blue-600 text-white py-2 px-5 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {lookupBusy ? 'Looking up…' : 'Look up'}
          </button>
        </form>
        {lookupError && <div className="mt-4 p-3 rounded-md bg-red-100 text-red-700">{lookupError}</div>}
        {lookupResult && (
          <dl className="mt-4 grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-gray-500">Email</dt>
            <dd className="text-gray-900">{lookupResult.email || '—'}</dd>
            <dt className="text-gray-500">UID</dt>
            <dd className="font-mono text-xs text-gray-700 self-center break-all">{lookupResult.uid}</dd>
            <dt className="text-gray-500">Status</dt>
            <dd className={lookupResult.disabled ? 'text-red-600 font-medium' : 'text-green-700'}>
              {lookupResult.disabled ? 'Blocked' : 'Active'}
            </dd>
            <dt className="text-gray-500">Created</dt>
            <dd className="text-gray-700">{fmtDate(lookupResult.created)}</dd>
            <dt className="text-gray-500">Last sign-in</dt>
            <dd className="text-gray-700">{fmtDate(lookupResult.lastSignIn)}</dd>
          </dl>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Block User</h2>
        <p className="text-gray-600 mb-4">
          Disables the account by UID or email. The user is signed out and can no longer create previews or invitations.
        </p>
        <form onSubmit={handleBlock} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="User UID or email"
            disabled={!!busy}
          />
          <button
            type="submit"
            disabled={!!busy}
            className="bg-red-600 text-white py-2 px-5 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && busy === identifier.trim() ? 'Blocking…' : 'Block user'}
          </button>
        </form>
        {message && <div className="mt-4 p-3 rounded-md bg-green-100 text-green-700">{message}</div>}
        {error && <div className="mt-4 p-3 rounded-md bg-red-100 text-red-700">{error}</div>}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Blocked Users ({users.length})</h2>
          <button
            onClick={load}
            disabled={listing}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {listing ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {!listing && users.length === 0 ? (
          <p className="text-gray-500 text-sm">No blocked users.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">UID</th>
                  <th className="py-2 pr-4 font-medium">Last sign-in</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.uid} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{u.email || '—'}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-gray-600">{u.uid}</td>
                    <td className="py-2 pr-4 text-gray-600">{fmtDate(u.lastSignIn)}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => handleUnblock(u)}
                        disabled={!!busy}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {busy === u.uid ? '…' : 'Unblock'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Block IP</h2>
        <p className="text-gray-600 mb-4">
          Refuses requests from this IP to previews, invitations, checkout, the contact form and sign-in links. Takes effect within about 30 seconds.
        </p>
        <form onSubmit={handleBlockIp} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            className="sm:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="IP address"
            disabled={!!ipBusy}
          />
          <input
            type="text"
            value={ipNote}
            onChange={(e) => setIpNote(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Note (optional)"
            disabled={!!ipBusy}
          />
          <button
            type="submit"
            disabled={!!ipBusy || !ipInput.trim()}
            className="bg-red-600 text-white py-2 px-5 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Block IP
          </button>
        </form>
        {ipMessage && <div className="mt-4 p-3 rounded-md bg-green-100 text-green-700">{ipMessage}</div>}
        {ipError && <div className="mt-4 p-3 rounded-md bg-red-100 text-red-700">{ipError}</div>}

        <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Blocked IPs ({ips.length})</h3>
        {ips.length === 0 ? (
          <p className="text-gray-500 text-sm">No blocked IPs.</p>
        ) : (
          <table className="min-w-full text-sm">
            <tbody>
              {ips.map(r => (
                <tr key={r.ip} className="border-b border-gray-100">
                  <td className="py-2 pr-4 font-mono text-xs text-gray-900">{r.ip}</td>
                  <td className="py-2 pr-4 text-gray-600">{r.note || '—'}</td>
                  <td className="py-2 pr-4 text-gray-500">{fmtDate(r.blockedAt)}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => handleUnblockIp(r.ip)}
                      disabled={!!ipBusy}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {ipBusy === r.ip ? '…' : 'Unblock'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminBlockedUsers;
